import React, { useMemo, useState } from 'react';
import { Button, Drawer, Grid, Select, Space, TableProps } from 'antd';
import { useMakeSelect, useModel } from '../../../../../../Hooks/TT_ELD/Makes-Models';
import ModelTable from './ModelTable';
import AddModelList from './AddModelList';
import SearchOptions from '../../../../../../Utils/SearchOptions';
import { SearchResultForModel } from '../../../../../../Utils/SearchResults';
import useRouteQuery from '../../../../../../Utils/useRouterQuery';
import { useTagQuerySync } from '../../../../../../Utils/useTagQuerySync';
import { TableParams } from '../../../../../../Utils/TableTypes';
import { FilterOutlined } from '@ant-design/icons';

const { useBreakpoint } = Grid;

type Data = {
  data?: { data: Array<any>; count: number };
  isLoading?: boolean;
  refetch?: any;
  isFetching?: any;
};

const Model = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [filtersOpen, setFiltersOpen] = useState(false);

  const [makeId, setMakeId] = useState<string>('');
  const [modelName, setModelName] = useRouteQuery('', 'modelName', (v) => String(v));
  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: { current: 1, pageSize: 10 },
  });

  useTagQuerySync(
    { en_US: 'Models', zh_CN: '' },
    'models',
    { modelName, makeId, tableParams },
    { modelName: setModelName, makeId: setMakeId, tableParams: setTableParams },
  );

  const { data, isLoading, isFetching, refetch } = useModel(modelName, makeId, tableParams);
  const make: Data = useMakeSelect();

  const handleTableChange: TableProps<any>['onChange'] = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      filters,
      sortOrder: Array.isArray(sorter) ? undefined : sorter.order,
      sortField: Array.isArray(sorter) ? undefined : sorter.field,
    });
  };

  const handleSearchSelect =
    (setter: (val: string) => void, nameSetter?: (val: string) => void) =>
    (val: any, { valId }: { valId: string }) => {
      setter(valId);
      nameSetter?.(val);
      if (valId) {
        setModelName(val);
        setTableParams({ pagination: { current: 1 } });
      }
    };

  const [open, setOpen] = useState(false);

  const filtersUI = useMemo(
    () => (
      <Space
        direction={isMobile ? 'vertical' : 'horizontal'}
        size={10}
        style={{
          width: '100%',
          display: 'flex',
          flexWrap: isMobile ? 'nowrap' : 'wrap',
          justifyContent: 'flex-start',
        }}
      >
        <div
          style={{ flex: isMobile ? '1 1 100%' : '0 0 280px', minWidth: isMobile ? '100%' : 260 }}
        >
          <SearchOptions
            SearchResult={SearchResultForModel}
            onSelect={handleSearchSelect(setModelName)}
            placeholder="Search by model"
            value={modelName}
          />
        </div>

        <div
          style={{ flex: isMobile ? '1 1 100%' : '0 0 280px', minWidth: isMobile ? '100%' : 260 }}
        >
          {make?.data?.data && (
            <Select
              allowClear
              style={{ width: 300 }}
              showSearch
              placeholder="Filter by make"
              optionFilterProp="children"
              value={makeId || undefined}
              onChange={(val) => {
                setMakeId(val ?? '');
                setTableParams({ pagination: { current: 1 } });
              }}
              filterOption={(input, option: any) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={make?.data?.data?.map((option) => ({
                value: option.id,
                label: option.name,
              }))}
            />
          )}
        </div>

        {isMobile && (
          <Space style={{ width: '100%' }} size={10}>
            <Button block onClick={() => setFiltersOpen(false)}>
              Close
            </Button>
            <Button
              block
              type="primary"
              onClick={() => {
                refetch();
                setFiltersOpen(false);
              }}
            >
              Apply & Refresh
            </Button>
          </Space>
        )}
      </Space>
    ),
    [isMobile, make?.data?.data, makeId, modelName],
  );

  return (
    <div className="mainPage">
      {open && <AddModelList open={open} setOpen={setOpen} dataRefetch={refetch} />}

      {/* Toolbar */}
      {!isMobile ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
          }}
        >
          <div style={{ flex: 1 }}>{filtersUI}</div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <Button type="primary" size="large" onClick={() => setOpen(true)}>
              Add Model
            </Button>
            <Button size="large" onClick={() => refetch()}>
              Refresh
            </Button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 10 }}>
          <Button
            block
            size="large"
            onClick={() => setFiltersOpen(true)}
            icon={<FilterOutlined />}
          />

          <Button block type="primary" size="large" onClick={() => setOpen(true)}>
            Add Model
          </Button>
          <Button block size="large" onClick={() => refetch()}>
            Refresh
          </Button>
        </div>
      )}

      {/* Mobile filters drawer */}
      <Drawer
        title="Filters"
        placement="bottom"
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        height="55vh"
      >
        {filtersUI}
      </Drawer>

      <ModelTable
        model={data?.data}
        total={data?.count}
        isLoading={isLoading}
        isFetching={isFetching}
        refetch={refetch}
        handleTableChange={handleTableChange}
        tableParams={tableParams}
      />
    </div>
  );
};

export default Model;
